import { useCallback, useEffect } from 'react'
import { Platform } from 'react-native'
import { useApolloClient, useSubscription } from '@apollo/client'
import { onWebSocketConnected } from '../../../API'
import {
  shareDocletDraftVersionQuery,
  shareDocletUpdatedSubscription
} from '../api'
import { RemoteSave } from './useDocletAutosave'

/**
 * Tells the builder about draft saves on the server so it can pause before
 * conflicting: live via the shareDocletUpdated subscription, plus a version
 * check whenever the tab regains focus or the websocket reconnects, since
 * subscriptions don't replay events missed while disconnected or asleep.
 */
const useRemoteDocletSaves = (
  docletId: string,
  onRemoteSave: (remote: RemoteSave) => void
) => {
  const client = useApolloClient()

  useSubscription(shareDocletUpdatedSubscription, {
    variables: { docletId },
    fetchPolicy: 'no-cache',
    onData: ({ data }) => {
      const update = data?.data?.shareDocletUpdated
      if (update) {
        onRemoteSave({
          draftVersion: update.draftVersion,
          by: update.draftUpdatedBy ?? null
        })
      }
    }
  })

  const checkVersion = useCallback(async () => {
    try {
      const { data } = await client.query({
        query: shareDocletDraftVersionQuery,
        variables: { id: docletId },
        fetchPolicy: 'network-only'
      })
      const doclet = data?.shareDoclet
      if (doclet) {
        onRemoteSave({
          draftVersion: doclet.draftVersion,
          by: doclet.draftUpdatedBy ?? null
        })
      }
    } catch {
      // Best effort; the save-time version check still applies
    }
  }, [client, docletId, onRemoteSave])

  useEffect(() => onWebSocketConnected(checkVersion), [checkVersion])

  useEffect(() => {
    if (Platform.OS !== 'web') return
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkVersion()
    }
    window.addEventListener('focus', checkVersion)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', checkVersion)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [checkVersion])

  return checkVersion
}

export default useRemoteDocletSaves
